import React, { useState } from 'react';
import {
  X,
  History,
  Activity,
  Clock,
  RotateCcw,
  CheckCircle2,
  Lock,
  Unlock,
  Share2,
  Trash2,
  Edit3,
  FilePlus,
  ShieldCheck,
  Eye,
  AlertCircle,
  FileText,
  User,
} from 'lucide-react';

export default function IdentityActivityModal({
  open,
  onClose,
  identity,
  onRestoreVersion,
}) {
  const [activeTab, setActiveTab] = useState('versions'); // 'versions' | 'audit'
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(null);
  const [restoring, setRestoring] = useState(false);

  if (!open || !identity) return null;

  const f = identity.fields || identity;

  const versions =
    Array.isArray(identity.versions) && identity.versions.length > 0
      ? identity.versions
      : [
          {
            version: 1,
            savedAt: identity.updated_at || identity.created_at || new Date().toISOString(),
            title: identity.title || 'Untitled Identity',
            summary: 'Initial identity record created',
            fields: {
              fullName: f.fullName,
              documentNumber: f.documentNumber,
              expiryDate: f.expiryDate,
              issuingAuthority: f.issuingAuthority,
            },
          },
        ];

  const auditLog =
    Array.isArray(identity.auditLog) && identity.auditLog.length > 0
      ? identity.auditLog
      : [
          {
            action: 'Created',
            actor: 'You (Current Device)',
            timestamp: identity.created_at || new Date().toISOString(),
            details: 'Zero-knowledge encrypted identity credential created with AES-256-GCM',
          },
          ...(identity.updated_at && identity.updated_at !== identity.created_at
            ? [
                {
                  action: 'Updated',
                  actor: 'You (Current Device)',
                  timestamp: identity.updated_at,
                  details: 'Identity details updated and re-encrypted',
                },
              ]
            : []),
          ...(identity.attachments && identity.attachments.length > 0
            ? [
                {
                  action: 'Document Attached',
                  actor: 'You (Current Device)',
                  timestamp: identity.attachments[0].uploadedAt || identity.updated_at || new Date().toISOString(),
                  details: `${identity.attachments.length} encrypted document scan(s) uploaded`,
                },
              ]
            : []),
        ];

  const selectedVersion =
    selectedVersionIndex !== null ? versions[selectedVersionIndex] : versions[versions.length - 1];

  const handleRestore = async () => {
    if (!selectedVersion || !onRestoreVersion) return;
    setRestoring(true);
    try {
      await onRestoreVersion(identity, selectedVersion);
      onClose();
    } catch (err) {
      console.error('Error restoring identity version:', err);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer identity-drawer ios-identity-drawer identity-activity-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Activity and Version History"
      >
        {/* Header */}
        <div className="card-drawer-header modal-header identity-activity-header">
          <div className="identity-activity-title-group">
            <div className="identity-header-avatar">
              <History size={20} />
            </div>
            <div>
              <h2 className="modal-title">Activity & Version History</h2>
              <p className="modal-subtitle">
                Inspect audit events and rollback changes for "{identity.title}"
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close activity dialog"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="identity-editor-tabs-bar">
          <button
            type="button"
            className={`identity-editor-tab-btn ${activeTab === 'versions' ? 'active' : ''}`}
            onClick={() => setActiveTab('versions')}
          >
            <Clock size={15} />
            <span>Version History ({versions.length})</span>
          </button>
          <button
            type="button"
            className={`identity-editor-tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <Activity size={15} />
            <span>Audit Trail ({auditLog.length})</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="card-drawer-body modal-body identity-activity-body">
          {activeTab === 'versions' && (
            <div className="identity-versions-layout">
              {/* Version list column */}
              <div className="identity-versions-sidebar">
                <div className="identity-versions-list">
                  {versions.map((v, idx) => {
                    const isSelected =
                      selectedVersionIndex === idx ||
                      (selectedVersionIndex === null && idx === versions.length - 1);
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`identity-version-item ${isSelected ? 'active' : ''}`}
                        onClick={() => setSelectedVersionIndex(idx)}
                      >
                        <div className="identity-version-num">
                          <span>v{v.version || idx + 1}</span>
                          {idx === versions.length - 1 && (
                            <span className="badge badge-primary">Current</span>
                          )}
                        </div>
                        <div className="identity-version-time">
                          {new Date(v.savedAt).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        <div className="identity-version-summary">
                          {v.summary || 'Identity updated'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Version preview column */}
              <div className="identity-version-preview">
                {selectedVersion ? (
                  <div className="identity-version-preview-content">
                    <div className="identity-version-preview-header">
                      <div>
                        <h4>Snapshot v{selectedVersion.version || (selectedVersionIndex !== null ? selectedVersionIndex + 1 : versions.length)}</h4>
                        <span className="text-secondary text-sm">
                          Captured on {new Date(selectedVersion.savedAt).toLocaleString()}
                        </span>
                      </div>
                      {selectedVersionIndex !== null && selectedVersionIndex !== versions.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={handleRestore}
                          disabled={restoring}
                        >
                          <RotateCcw size={14} />
                          <span>{restoring ? 'Restoring...' : 'Rollback to this version'}</span>
                        </button>
                      )}
                    </div>

                    <div className="identity-version-preview-box">
                      <div className="preview-field-row">
                        <span className="preview-label">Title:</span>
                        <span className="preview-value">{selectedVersion.title || identity.title}</span>
                      </div>
                      {selectedVersion.fields && (
                        <>
                          {selectedVersion.fields.fullName && (
                            <div className="preview-field-row">
                              <span className="preview-label">Full Name:</span>
                              <span className="preview-value">{selectedVersion.fields.fullName}</span>
                            </div>
                          )}
                          {selectedVersion.fields.documentNumber && (
                            <div className="preview-field-row">
                              <span className="preview-label">Doc Number:</span>
                              <span className="preview-value">•••• •••• •••• {selectedVersion.fields.documentNumber.slice(-4)}</span>
                            </div>
                          )}
                          {selectedVersion.fields.issuingAuthority && (
                            <div className="preview-field-row">
                              <span className="preview-label">Issuer:</span>
                              <span className="preview-value">{selectedVersion.fields.issuingAuthority}</span>
                            </div>
                          )}
                          {selectedVersion.fields.expiryDate && (
                            <div className="preview-field-row">
                              <span className="preview-label">Expiry Date:</span>
                              <span className="preview-value">{new Date(selectedVersion.fields.expiryDate).toLocaleDateString()}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="identity-version-empty">
                    <History size={36} opacity={0.3} />
                    <p>Select a version snapshot to preview changes.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="identity-audit-list">
              {auditLog.map((item, idx) => (
                <div key={idx} className="identity-audit-item">
                  <div className="identity-audit-icon-box">
                    {item.action === 'Created' && <FilePlus size={16} className="text-success" />}
                    {item.action === 'Updated' && <Edit3 size={16} className="text-primary" />}
                    {item.action === 'Document Attached' && <ShieldCheck size={16} className="text-info" />}
                    {item.action === 'Shared' && <Share2 size={16} className="text-warning" />}
                    {item.action === 'Locked' && <Lock size={16} className="text-secondary" />}
                    {!['Created', 'Updated', 'Document Attached', 'Shared', 'Locked'].includes(item.action) && (
                      <CheckCircle2 size={16} className="text-primary" />
                    )}
                  </div>
                  <div className="identity-audit-content">
                    <div className="identity-audit-row">
                      <span className="identity-audit-action">{item.action}</span>
                      <span className="identity-audit-time">
                        {new Date(item.timestamp).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="identity-audit-details">{item.details}</p>
                    <span className="identity-audit-actor">{item.actor}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="card-drawer-footer modal-footer identity-activity-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </aside>
    </div>
  );
}
